/**
 * "Up for automatic re-vote" — the two provisional rules, as the room needs to read them.
 *
 * This started as a bulleted list of `title — summary`, which on a desktop looked like a list and
 * on a phone looked like a wall. The title carried the vote banner, the summary opened by
 * repeating it, and then ran for a hundred and fifty words without a paragraph break. A GM
 * checking the agenda on his phone had to read four lines of shouting to find out which rule he
 * was looking at.
 *
 * So the row has a hierarchy now: what it is called, what happens to it, who proposed it, and —
 * behind a disclosure on small screens — what it actually says. The banner and byline come out of
 * the title into chips (`splitRuleTitle`), which is presentation and needs nobody's permission.
 *
 * The edit button is the other half. These items have no author in the app to fix their wording,
 * so until now nobody could: they are generated rows in `rulebook.json`. A commissioner may now
 * rewrite them under 2019-I-2 §2 — recorded as an override, never silently, and always showing
 * the proposer that it happened so he can exercise the 2020-I-1 §2 veto he is owed.
 */

import { useState } from 'react';
import { OverrideError, makeOverride, type Override } from '../domain/overrides.js';
import { revoteTarget, revoteWording } from '../domain/revote.js';
import type { RulebookEntry } from '../domain/rulebook.js';
import { isEmptyRich, plainToRich, richToPlain, sanitizeRich } from '../domain/richText.js';
import { RichEditor, RichView } from './Rich.js';
import { managerShort } from '../domain/identity.js';

export function RevoteItems(
  { rules, overrides, commish, actor, season, onSave }:
  {
    rules: readonly RulebookEntry[];
    overrides: readonly Override[];
    commish: boolean;
    actor: string;
    season: number;
    onSave: (o: Override) => Promise<void> | void;
  },
) {
  const [editing, setEditing] = useState<string | null>(null);

  return (
    <ul className="revotelist">
      {rules.map((r) => (
        <RevoteRow key={r.id} rule={r} overrides={overrides} commish={commish} actor={actor}
          season={season} onSave={onSave}
          editing={editing === r.id} onEdit={() => setEditing(r.id)}
          onDone={() => setEditing(null)} idle={editing === null} />
      ))}
    </ul>
  );
}

function RevoteRow(
  { rule, overrides, commish, actor, season, onSave, editing, onEdit, onDone, idle }: {
    rule: RulebookEntry; overrides: readonly Override[]; commish: boolean;
    actor: string; season: number; onSave: (o: Override) => Promise<void> | void;
    editing: boolean; onEdit: () => void; onDone: () => void; idle: boolean;
  },
) {
  const [open, setOpen] = useState(false);
  const w = revoteWording(rule, overrides);
  /*
   * The disclosure is offered on length, not on a flag. Both of these rules happen to be long;
   * a future provisional rule might be two sentences, and a "read the whole rule" button under
   * two sentences is a button that lies about there being more.
   */
  const long = richToPlain(w.summary).split(/\s+/).length > 40;

  return (
    <li className="revoteitem">
      <div className="revotetop">
        <span className="ridchip">{rule.id}</span>
        <span className="chip warning">Lapses without 7</span>
        {w.proposer && <span className="revoteby">proposed by {w.proposer}</span>}
      </div>

      {editing ? (
        <RevoteEditor rule={rule} wording={w} actor={actor} season={season}
          onCancel={onDone}
          onSave={async (os) => { for (const o of os) await onSave(o); onDone(); }} />
      ) : (
        <>
          <b className="revotetitle">{w.title}</b>
          <RichView html={w.summary} className={`revotebody ${open ? 'open' : ''}`} />
          {w.edited && (
            <p className="note revoteedited">
              Wording tidied by {managerShort(w.editedBy ?? '')}
              {w.reason ? ` — ${w.reason}` : ''}.
              {w.proposer && ` ${w.proposer} may refuse it under 2020-I-1 §2.`}
            </p>
          )}
          <div className="revoteacts">
            {long && (
              <button type="button" className="linkbtn tiny" aria-expanded={open}
                onClick={() => setOpen(!open)}>
                {open ? 'show less' : 'read the whole rule'}
              </button>
            )}
            {commish && idle && (
              <button type="button" className="linkbtn tiny" onClick={onEdit}>
                {w.edited ? 'edit the wording again' : 'edit the wording'}
              </button>
            )}
            {commish && idle && w.edited && (
              <button type="button" className="linkbtn tiny"
                onClick={() => { void revert(w.applied, actor, onSave); }}>
                put the rulebook wording back
              </button>
            )}
          </div>
        </>
      )}
    </li>
  );
}

async function revert(
  applied: readonly Override[], actor: string, onSave: (o: Override) => Promise<void> | void,
) {
  const at = new Date().toISOString();
  for (const o of applied) await onSave({ ...o, revertedAt: at, revertedBy: actor.toUpperCase() });
}

function RevoteEditor(
  { rule, wording, actor, season, onCancel, onSave }: {
    rule: RulebookEntry;
    wording: ReturnType<typeof revoteWording>;
    actor: string; season: number;
    onCancel: () => void;
    onSave: (os: Override[]) => void;
  },
) {
  const [title, setTitle] = useState(wording.title);
  const [summary, setSummary] = useState(
    wording.summary.startsWith('<') ? wording.summary : plainToRich(wording.summary),
  );
  const [reason, setReason] = useState('');
  const [failed, setFailed] = useState<string | null>(null);

  function commit() {
    const now = new Date().toISOString();
    const body = sanitizeRich(summary);
    const out: Override[] = [];
    try {
      if (!title.trim()) throw new OverrideError('A rule going to a vote needs a title.');
      if (isEmptyRich(body)) throw new OverrideError('The summary cannot be empty.');
      if (title.trim() !== wording.title) {
        out.push(makeOverride({
          target: revoteTarget(rule.id, 'title'),
          label: `Re-vote wording — ${wording.original.title}`,
          ruleValue: wording.original.title,
          setTo: title.trim(),
          reason,
          cites: ['2019-I-2', '2020-I-1'],
        }, actor, season, now, `${revoteTarget(rule.id, 'title')}-${now}`));
      }
      if (body !== wording.summary) {
        out.push(makeOverride({
          target: revoteTarget(rule.id, 'summary'),
          label: `Re-vote summary — ${wording.original.title}`,
          ruleValue: wording.original.summary,
          setTo: body,
          reason,
          cites: ['2019-I-2', '2020-I-1'],
        }, actor, season, now, `${revoteTarget(rule.id, 'summary')}-${now}`));
      }
      if (!out.length) throw new OverrideError('Nothing has changed.');
    } catch (e) {
      setFailed(e instanceof OverrideError ? e.message : String(e));
      return;
    }
    onSave(out);
  }

  return (
    <div className="formstack revoteform">
      <label>
        Title
        <input value={title} onChange={(e) => setTitle(e.target.value)} />
      </label>
      <div className="richfield">
        <span className="richlabel">What it says</span>
        <RichEditor value={summary} label="What it says"
          onChange={setSummary} />
      </div>
      <label>
        Why the change?
        <input value={reason} placeholder="Tightened the second apron wording — same effect"
          onChange={(e) => setReason(e.target.value)} />
      </label>
      <p className="note tight">
        Recorded as an edit by you, against the rulebook text, and shown to the league on the
        docket. {wording.proposer ?? 'The proposer'} may refuse it under 2020-I-1 §2.
      </p>
      {failed && <p className="note bad">{failed}</p>}
      <div className="actions">
        <button type="button" className="linkbtn" onClick={onCancel}>cancel</button>
        <button type="button" className="btn primary" onClick={commit}>Save the wording</button>
      </div>
    </div>
  );
}
